import React, { useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AuthLayout from "../auth/AuthLayout";
import { PasswordField, StrengthMeter } from "../auth/AuthControls";
import { getSetupStatus, runSetup } from "../../actions";
import useBranding from "../../utils/branding";

const MIN_PASSWORD_LENGTH = 10;
const EMPTY = { token: "", workspaceName: "", firstName: "", lastName: "", email: "", password: "", databaseName: "" };

// A new instance: create its first admin and first workspace. Needs the
// SETUP_TOKEN from the server's .env; refused once an admin exists.
export default function SetupPage() {
  const history = useHistory();
  const branding = useBranding();
  const [status, setStatus] = useState(null);
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getSetupStatus().then((res) => setStatus(res?.success ? res.content : { error: res?.msg || "Couldn't reach the server." }));
  }, []);

  const set = (key) => (event) => setValues((current) => ({ ...current, [key]: event.target.value }));
  const passwordShort = values.password && values.password.length < MIN_PASSWORD_LENGTH;
  const complete =
    values.token.trim() && values.workspaceName.trim() && values.firstName.trim() && values.lastName.trim() && values.email.trim() && values.password.length >= MIN_PASSWORD_LENGTH;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const res = await runSetup(values);
    setBusy(false);
    if (res?.success) history.push("/login?notice=setup");
    else setError(res?.msg || "Setup didn't work.");
  };

  return (
    <AuthLayout maxWidth={520}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Set up {branding.productName}
      </Typography>

      {!status ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} />
          ))}
        </Stack>
      ) : status.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {status.error}
        </Alert>
      ) : !status.needed ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography color="text.secondary">This instance is already set up. Sign in with your account.</Typography>
          <Button component={RouterLink} to="/login" variant="contained" size="large">
            Go to sign in
          </Button>
        </Stack>
      ) : !status.enabled ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Setup is switched off on this server. Set SETUP_TOKEN (16 characters or more) in the server's .env, restart it, and reload this page.
        </Alert>
      ) : (
        <form onSubmit={submit} noValidate>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create the first admin and the first workspace. You need the setup token from the server's .env file.
          </Typography>
          <Stack spacing={2}>
            <PasswordField label="Setup token" value={values.token} onChange={set("token")} autoComplete="off" autoFocus />
            <TextField label="First workspace" value={values.workspaceName} onChange={set("workspaceName")} placeholder="e.g. your company" inputProps={{ maxLength: 80 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="First name" value={values.firstName} onChange={set("firstName")} autoComplete="given-name" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Last name" value={values.lastName} onChange={set("lastName")} autoComplete="family-name" />
              </Grid>
            </Grid>
            <TextField label="Admin email" type="email" value={values.email} onChange={set("email")} autoComplete="email" />
            <div>
              <PasswordField
                label="Password"
                value={values.password}
                onChange={set("password")}
                autoComplete="new-password"
                error={Boolean(passwordShort)}
                helperText={passwordShort ? `At least ${MIN_PASSWORD_LENGTH} characters.` : " "}
              />
              <StrengthMeter value={values.password} />
            </div>
            <TextField
              label="First database (optional)"
              value={values.databaseName}
              onChange={set("databaseName")}
              helperText="A database on the main Neo4j server for this workspace. Leave empty to add one later under Databases."
            />
            {error && <Alert severity="error">{error}</Alert>}
            <LoadingButton type="submit" variant="contained" size="large" loading={busy} disabled={!complete}>
              Create admin and workspace
            </LoadingButton>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}
